import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import { useTeams } from "../../../contexts/Teams";
import { ReactComponent as AddIcon } from "../../../assets/Icons/add.svg";
import { useCommon } from "../../../contexts/common";
import Grid from "@mui/material/Grid";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { professionalLicenseSchema } from "../../../validations/registerValidation";
import ProfessionalLicenseForm from "../../Onboarding/ProfessionalLicense/ProfessionalLicenseForm";
import CustomButton from "../../CustomButton";
import "./style.css";
import { trackClickEvent } from "../../../mixpanel";

const ProfessionalLicense = ({ role }) => {
  const {
    next,
    invitationState,
    updateInvitationState,
  } = useTeams();
  const { fetchLicensesList, licensesList, statesList, fetchStatesList } =
    useCommon();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const initialState = {
    has_license: false,
    license_type_id: null,
    license_state: 0,
    licensing_board: "",
    license_number: "",
    license_year: "",
    supervisor_name: "",
    supervisor_license_type_id: null,
    supervisor_license_state: 0,
    has_supervision: false,
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    trigger,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(professionalLicenseSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      licenses: invitationState?.licenses || [initialState],
    },
  });

  const watchedLicenses = watch("licenses");

  useEffect(() => {
    fetchLicensesList();
    fetchStatesList();
    if (!invitationState?.licenses?.length) {
      updateInvitationState({ licenses: [initialState] });
    }
  }, []);

  useEffect(() => {
    reset({
      licenses: invitationState?.licenses || [initialState],
    });
  }, [invitationState]);

  const handleChange = async (selected, index) => {
    const { name, value } = selected.target;
    const slicedName = name.split(".");
    const updatedLicenses = invitationState.licenses.map((license, i) =>
      i === index ? { ...license, [slicedName[2]]: value } : license
    );

    updateInvitationState({
      licenses: updatedLicenses,
    });
    setValue(`licenses.${index}.${slicedName[2]}`, value);
    await trigger(`licenses.${index}.${slicedName[2]}`);
  };

  const handleContinueClick = () => {
    setShowValidationErrors(true);
    handleSubmit(onSubmit)();
  };

  const handleLicenseChange = async (index, hasLicense) => {
    const updatedLicenses = invitationState.licenses.map((license, i) =>
      i === index
        ? {
            ...license,
            has_license: hasLicense,
            license_type_id: hasLicense ? license.license_type_id : null,
            license_state: hasLicense ? license.license_state : 0,
            licensing_board: hasLicense ? license.licensing_board : "",
            license_number: hasLicense ? license.license_number : "",
            license_year: hasLicense ? license.license_year : "",
          }
        : license
    );

    updateInvitationState({
      licenses: updatedLicenses,
    });
    setValue(`licenses.${index}.has_license`, hasLicense);
    if (!hasLicense) {
      setValue(`licenses.${index}.license_type_id`, null);
      setValue(`licenses.${index}.license_state`, 0);
      setValue(`licenses.${index}.licensing_board`, "");
      setValue(`licenses.${index}.license_number`, "");
      setValue(`licenses.${index}.license_year`, "");
    }
    // await trigger(`licenses.${index}`);
  };

  const handleSupervisionChange = async (index, hasSupervision) => {
    const updatedLicenses = invitationState.licenses.map((license, i) =>
      i === index
        ? {
            ...license,
            has_supervision: hasSupervision,
            supervisor_name: hasSupervision ? license.supervisor_name : "",
            supervisor_license_type_id: hasSupervision
              ? license.supervisor_license_type_id
              : null,
            supervisor_license_state: hasSupervision
              ? license.supervisor_license_state
              : 0,
          }
        : license
    );

    updateInvitationState({
      licenses: updatedLicenses,
    });
    setValue(`licenses.${index}.has_supervision`, hasSupervision);
    if (!hasSupervision) {
      setValue(`licenses.${index}.supervisor_name`, "");
      setValue(`licenses.${index}.supervisor_license_type_id`, null);
      setValue(`licenses.${index}.supervisor_license_state`, 0);
    }
    // await trigger(`licenses.${index}`);
  };

  const handleDeleteLicense = (indexToDelete) => {
    const updatedLicenses = invitationState.licenses.filter(
      (_, index) => index !== indexToDelete
    );
    updateInvitationState({
      licenses: updatedLicenses,
    });
    if (expandedIndex === indexToDelete) {
      setExpandedIndex(null);
    } else if (expandedIndex > indexToDelete) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const onSubmit = async () => {
    if (isValid) {
      trackClickEvent("Invitation - Professional License", {
        page: "Invitation Onboarding",
        button: "Continue",
        role,
      });
      next();
    } else {
      trigger();
    }
  };

  const addNew = async () => {
    const isTriggered = await trigger();
    if (isValid && isTriggered) {
      updateInvitationState({
        licenses: [...(invitationState.licenses || []), { ...initialState }],
      });
      setExpandedIndex(null);
      setShowValidationErrors(false);
    } else {
      await trigger();
    }
  };

  const hasValidCompletedSection = () => {
    const updates =
      watchedLicenses?.every((license, index) => {
        const licenseErrors = errors?.licenses?.[index];

        // Check if license section is complete and valid
        const hasCompleteLicense =
          license?.has_license &&
          license?.license_type_id &&
          license?.license_state &&
          license?.licensing_board?.trim() &&
          license?.license_number?.trim() &&
          license?.license_year?.trim() &&
          !licenseErrors?.license_type_id &&
          !licenseErrors?.license_state &&
          !licenseErrors?.licensing_board &&
          !licenseErrors?.license_number &&
          !licenseErrors?.license_year;

        // Check if supervision section is complete and valid
        const hasCompleteSupervision =
          license?.has_supervision &&
          license?.supervisor_name?.trim() &&
          license?.supervisor_license_type_id &&
          license?.supervisor_license_state &&
          !licenseErrors?.supervisor_name &&
          !licenseErrors?.supervisor_license_type_id &&
          !licenseErrors?.supervisor_license_state;

        // A license is considered complete if it has either a complete license OR complete supervision
        return hasCompleteLicense || hasCompleteSupervision;
      }) && watchedLicenses?.length > 0;
    return updates;
  };

  const toggleExpanded = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Box>
        <div className="professional-license">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h4" mb={1} fontWeight={600}>
                Professional License
              </Typography>
              <Typography variant="body1" mb={3}>
                What is your current license status?
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {invitationState?.licenses?.map((license, index) => (
                <Box
                // sx={{
                //   mt:
                //     index > 0 &&
                //     index === invitationState?.licenses.length - 1
                //       ? "100px"
                //       : "0px",
                // }}
                >
                  {index > 0 &&
                    index === invitationState?.licenses.length - 1 && (
                      <Typography
                        variant="h6"
                        mb={1}
                        fontSize={20}
                        fontWeight={600}
                        mt={3}
                      >
                        Add New License
                      </Typography>
                    )}
                  <ProfessionalLicenseForm
                    key={index}
                    licensesList={licensesList}
                    statesList={statesList}
                    onChange={(selected) => handleChange(selected, index)}
                    onLicenseChange={(hasLicense) =>
                      handleLicenseChange(index, hasLicense)
                    }
                    onSupervisionChange={(hasSupervision) =>
                      handleSupervisionChange(index, hasSupervision)
                    }
                    onDelete={() => handleDeleteLicense(index)}
                    onToggleExpanded={() => toggleExpanded(index)}
                    showDeleteButton={invitationState.licenses.length > 1}
                    control={control}
                    license={license}
                    errors={errors}
                    index={index}
                    watchedLicense={watchedLicenses?.[index]}
                    isExpanded={expandedIndex === index}
                    isCollapsed={
                      invitationState.licenses.length > 1 &&
                      index < invitationState.licenses.length - 1
                    }
                    showValidationErrors={showValidationErrors}
                  />
                </Box>
              ))}
            </Grid>
          </Grid>
          {hasValidCompletedSection() && (
            <Button
              sx={{
                color: "primary.main",
                p: 1.125,
                minWidth: 0,
                mt: 3,
                mb: 0.5,
              }}
              onClick={addNew}
              variant="outlined"
            >
              <AddIcon width={18} height={18} />
              Add New License
            </Button>
          )}
        </div>
      </Box>
      <div className="stickyButton">
        <Box textAlign="center">
          <CustomButton
            className="btnContinue"
            variant="contained"
            sx={{ minWidth: 212 }}
            onClick={handleContinueClick}
            page="Register"
            name="Continue"
            eventName="U-Step 6 - Professional License"
          >
            Continue
          </CustomButton>
        </Box>
      </div>
    </Container>
  );
};

export default ProfessionalLicense;
